import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { config } from '../shared/config';

interface Verified {
  found: true;
  publicId: string;
  documentType: string;
  issuedOn: string | null;
  sha256: string | null;
  basis: 'public_record' | 'shared';
}

/** SHA-256 of a file, computed in this browser: the file is never uploaded. */
export async function fileSha256(file: Blob): Promise<string> {
  const bytes =
    typeof file.arrayBuffer === 'function'
      ? await file.arrayBuffer()
      : await new Promise<ArrayBuffer>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as ArrayBuffer);
          reader.onerror = () => reject(reader.error);
          reader.readAsArrayBuffer(file);
        });
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

const fmt = (iso: string | null) =>
  iso ? new Date(`${iso}T12:00:00Z`).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' }) : 'an unrecorded date';

/**
 * Public: check a LienGuardian document by the ID printed on it, and
 * optionally that a copy is byte-for-byte the file LienGuardian issued. Only
 * documents their contractor made verifiable (or recorded public records)
 * answer; the page never shows who or where.
 */
export default function Verify() {
  const params = useParams<{ id?: string }>();
  const [id, setId] = useState(params.id ?? '');
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<
    { kind: 'found'; doc: Verified; match: boolean | null } | { kind: 'none' } | { kind: 'error'; text: string } | null
  >(null);

  const check = async (rawId = id, copy = file) => {
    if (!rawId.trim()) return;
    setBusy(true);
    setResult(null);
    try {
      const res = await fetch(`${config.apiUrl}/verify/${encodeURIComponent(rawId.trim())}`);
      if (res.status === 404) {
        setResult({ kind: 'none' });
        return;
      }
      if (!res.ok) throw new Error(`The check did not complete (${res.status}).`);
      const doc = (await res.json()) as Verified;
      const match = copy && doc.sha256 ? (await fileSha256(copy)) === doc.sha256 : null;
      setResult({ kind: 'found', doc, match });
    } catch (err) {
      setResult({ kind: 'error', text: err instanceof Error ? err.message : 'The check did not complete.' });
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (params.id) void check(params.id, null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.id]);

  return (
    <div className="flex min-h-screen items-start justify-center bg-bg px-4 py-12">
      <div className="w-full max-w-lg space-y-6">
        <div>
          <p className="text-2xl font-extrabold tracking-tight text-ink">
            Lien<span className="text-tint">Guardian</span>
          </p>
          <h1 className="mt-4 text-xl font-bold text-ink">Verify a document</h1>
          <p className="mt-1 text-sm text-muted">
            Enter the ID printed along the bottom of the page. To check that a copy is unaltered, choose the PDF too: it is
            checked in your browser and never uploaded.
          </p>
        </div>

        <form
          className="space-y-3 rounded-lg border border-border bg-surface p-4"
          onSubmit={(e) => {
            e.preventDefault();
            void check();
          }}
        >
          <label className="block text-sm font-semibold text-ink">
            Document ID
            <input
              value={id}
              onChange={(e) => setId(e.target.value)}
              placeholder="LG-XXXXX-XXXXX"
              className="mt-1 w-full rounded-md border border-border bg-bg px-3 py-2 font-mono text-sm text-ink"
            />
          </label>
          <label className="block text-sm font-semibold text-ink">
            PDF copy (optional)
            <input
              type="file"
              accept="application/pdf"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              className="mt-1 block w-full text-sm text-muted"
            />
          </label>
          <button type="submit" disabled={busy || !id.trim()} className="rounded-lg bg-tint px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
            {busy ? 'Checking…' : 'Verify'}
          </button>
        </form>

        {result?.kind === 'found' && (
          <div className="space-y-2 rounded-lg border border-ok/40 bg-ok/10 p-4 text-sm text-ink">
            <p className="font-semibold">
              Genuine: a {result.doc.documentType} issued through LienGuardian on {fmt(result.doc.issuedOn)}.
            </p>
            <p className="text-xs text-muted">
              {result.doc.basis === 'public_record'
                ? 'This document has been recorded and is a public record.'
                : 'Its owner has made it verifiable.'}{' '}
              ID {result.doc.publicId}.
            </p>
            {result.match === true && <p className="font-semibold text-ok">The file you chose is exactly the document LienGuardian issued.</p>}
            {result.match === false && (
              <p className="font-semibold text-urgent">The file you chose is not identical to the document LienGuardian issued.</p>
            )}
          </div>
        )}
        {result?.kind === 'none' && (
          <div className="rounded-lg border border-border bg-surface p-4 text-sm text-muted">
            No verifiable document has this ID. Either the ID was typed differently from the page, or its owner has not made it
            verifiable.
          </div>
        )}
        {result?.kind === 'error' && <div className="rounded-lg border border-urgent/40 bg-urgent/10 p-4 text-sm text-urgent">{result.text}</div>}

        <p className="text-xs text-dim">
          LienGuardian confirms only that it issued a document and whether a copy matches. It does not show names, addresses
          or contents.
        </p>
      </div>
    </div>
  );
}
